import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { Network, FolderTree, Briefcase, UserPlus, ToggleLeft, Plus, BookOpen, Settings } from 'lucide-react';
import { StructuresTab } from '../components/org/StructuresTab';
import { PositionsTab } from '../components/org/PositionsTab';
import { AssignmentsTab } from '../components/org/AssignmentsTab';
import { ModulesTab } from '../components/org/ModulesTab';
import { CoursesTab } from '../components/org/CoursesTab';
import { ConfigTab } from '../components/org/ConfigTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrganizationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [selectedInst, setSelectedInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('structures');
  const canCreate = ['super_admin', 'co_admin'].includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/core/institutions`);
        setInstitutions(data);
        if (data.length > 0) {
          const userInst = data.find(i => i.id === (user?.university_id || user?.institution_id));
          setSelectedInst(userInst || data[0]);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="organization-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><Network className="w-5 h-5" /> Organisation</h1>
          <p className="text-sm text-gray-500 mt-0.5">Structures, postes, affectations et modules</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <Button size="sm" onClick={() => navigate('/new-institution')} data-testid="new-institution-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle Institution
            </Button>
          )}
          {institutions.length > 1 && (
            <Select value={selectedInst?.id || ''} onValueChange={v => setSelectedInst(institutions.find(i => i.id === v))}>
              <SelectTrigger className="w-64" data-testid="institution-selector"><SelectValue placeholder="Institution" /></SelectTrigger>
              <SelectContent>{institutions.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      {selectedInst && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{selectedInst.total_users || 0}</p><p className="text-xs text-gray-500">Utilisateurs</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{selectedInst.total_students || 0}</p><p className="text-xs text-gray-500">Etudiants</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{selectedInst.total_units || 0}</p><p className="text-xs text-gray-500">Structures</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{selectedInst.enabled_modules?.length || 0}</p><p className="text-xs text-gray-500">Modules</p></CardContent></Card>
        </div>
      )}

      {selectedInst && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full justify-start flex-wrap">
            <TabsTrigger value="structures" data-testid="tab-structures"><FolderTree className="w-4 h-4 mr-1.5" /> Structures</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses"><BookOpen className="w-4 h-4 mr-1.5" /> Cours</TabsTrigger>
            <TabsTrigger value="positions" data-testid="tab-positions"><Briefcase className="w-4 h-4 mr-1.5" /> Postes</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="tab-assignments"><UserPlus className="w-4 h-4 mr-1.5" /> Affectations</TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules"><ToggleLeft className="w-4 h-4 mr-1.5" /> Modules</TabsTrigger>
            {canCreate && <TabsTrigger value="config" data-testid="tab-config"><Settings className="w-4 h-4 mr-1.5" /> Config</TabsTrigger>}
          </TabsList>
          <TabsContent value="structures"><StructuresTab institutionId={selectedInst.id} /></TabsContent>
          <TabsContent value="courses"><CoursesTab institutionId={selectedInst.id} /></TabsContent>
          <TabsContent value="positions"><PositionsTab institutionId={selectedInst.id} /></TabsContent>
          <TabsContent value="assignments"><AssignmentsTab institutionId={selectedInst.id} /></TabsContent>
          <TabsContent value="modules"><ModulesTab institutionId={selectedInst.id} /></TabsContent>
          {canCreate && <TabsContent value="config"><ConfigTab institutionId={selectedInst.id} /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
};
